import React, {
  useEffect,
  useLayoutEffect,
  useContext,
  useState,
  forwardRef,
  useRef,
  useMemo
} from 'react';
import { createPortal } from 'react-dom';
import videojs, { VideoJsPlayer } from 'video.js';
import { useResponsive } from '@hooks/useResponsive';
import { AssetVersionItemDto } from '@api/Api';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { checkCanPlaySource } from '@helpers/checkCanPlaySource';
import { fitPinSize } from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import { FabricContext } from '@context/FabricContext';
import { usePlayer } from '@context/PlayerContext';
import ControlBar from '@pages/MediaViewer/AssetViewer/VideoCard/components/ControlBar/ControlBar';
import 'video.js/dist/video-js.css';

const videoJsOptions = {
  autoplay: false,
  aspectRatio: '16:9',
  fluid: true
};

// The card is remounted for every asset. The frame shown last is kept over
// the next video until its first frame is ready, instead of a black frame
let lastVideoFrame: { canvas: HTMLCanvasElement; hiddenAt: number } | null =
  null;
// Only when navigating, not when the viewer is opened again later
const LAST_VIDEO_FRAME_MAX_AGE_MS = 1000;
// On slow loads show the black player rather than another video for long
const LAST_VIDEO_FRAME_MAX_SHOWN_MS = 300;

function captureVideoFrame(video: HTMLVideoElement) {
  const { clientWidth, clientHeight, videoWidth, videoHeight } = video;
  if (video.readyState < 2 || !videoWidth || !clientWidth || !clientHeight) {
    return null;
  }
  const pixelRatio = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(clientWidth * pixelRatio);
  canvas.height = Math.round(clientHeight * pixelRatio);
  // Same placement as the video itself (contained in its box)
  const scale = Math.min(
    canvas.width / videoWidth,
    canvas.height / videoHeight
  );
  const width = videoWidth * scale;
  const height = videoHeight * scale;
  try {
    canvas
      .getContext('2d')
      ?.drawImage(
        video,
        (canvas.width - width) / 2,
        (canvas.height - height) / 2,
        width,
        height
      );
  } catch {
    return null;
  }
  canvas.style.cssText =
    'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;';
  return canvas;
}

const VideoCard = forwardRef<
  VideoJsPlayer,
  {
    onReady: () => void;
    onError: () => void;
    asset: AssetVersionItemDto;
    isPreparing: boolean;
    isAssetReady: boolean;
    setSelectedCollapse?: (collapse: string | null) => void;
  }
>(function VideoCard(
  { onReady, onError, asset, isPreparing, isAssetReady, setSelectedCollapse },
  ref
) {
  const { isMdWidth } = useResponsive();
  const {
    selectedCommentId,
    playerTime,
    setPlayerTime,
    registerPlaybackPlayer
  } = useAssetViewerState();
  const playerRef = useRef<VideoJsPlayer | null>(null);
  const [_player, setPlayer] = useState<VideoJsPlayer | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const { canvas } = useContext(FabricContext);
  const { registerPlayer } = usePlayer();

  useEffect(() => {
    if (!isAssetReady) return;
    if (!canvas) return;
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;
    const resizeObserver = new ResizeObserver(() => {
      const { width, height } = videoContainer.getBoundingClientRect();

      const scale = width / canvas.getWidth();
      const zoom = canvas.getZoom() * scale;
      canvas.setDimensions({
        width,
        height
      });

      canvas.setViewportTransform([zoom, 0, 0, zoom, 0, 0]);

      const pinScale = fitPinSize(1, asset.metadata.type, canvas);
      canvas.getObjects().forEach((obj) => {
        if (obj.type === 'pin') {
          obj.set({ scaleX: pinScale, scaleY: pinScale });
          (obj as { setCoords?: () => void }).setCoords?.();
        }
      });
      canvas.requestRenderAll();
      canvas.fire('viewport:zoom:changed');
    });
    resizeObserver.observe(videoContainer);

    return () => resizeObserver.disconnect();
  }, [isAssetReady, canvas, asset.metadata.type]);

  // Layout effect: the player and its control bar are in place before the
  // first paint (the card is remounted with every asset)
  useLayoutEffect(() => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;
    const videoElement = document.createElement('video');
    videoElement.className = 'video-js';
    videoElement.playsInline = true;
    videoContainer.prepend(videoElement);
    const player = videojs(videoElement, videoJsOptions, function ready() {
      // eslint-disable-next-line react/no-this-in-sfc
      this.on('error', () => {
        if (onError) onError();
      });
      if (ref && 'current' in ref) ref.current = this;
      else if (ref) ref(this);
      if (onReady) onReady();
    });
    playerRef.current = player;
    setPlayer(player);
    registerPlayer(player);

    const isVideo = asset.metadata.type === 'video';
    const previousFrame =
      lastVideoFrame &&
      Date.now() - lastVideoFrame.hiddenAt <= LAST_VIDEO_FRAME_MAX_AGE_MS
        ? lastVideoFrame.canvas
        : null;
    lastVideoFrame = null;
    let removePreviousFrame = () => undefined as void;
    if (previousFrame && isVideo) {
      // Below the control bar, which is rendered into the player later
      videoElement.after(previousFrame);
      const remove = () => previousFrame.remove();
      player.one('loadeddata', remove);
      const timeout = setTimeout(remove, LAST_VIDEO_FRAME_MAX_SHOWN_MS);
      removePreviousFrame = () => {
        clearTimeout(timeout);
        remove();
      };
    }

    return () => {
      if (isVideo) {
        const canvas = captureVideoFrame(videoElement);
        lastVideoFrame = canvas ? { canvas, hiddenAt: Date.now() } : null;
      }
      removePreviousFrame();
      if (ref && 'current' in ref) ref.current = null;
      else if (ref) ref(null);
      playerRef.current = null;
      setPlayer(null);
      registerPlayer(null);
      player.dispose();
      videoElement.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = playerRef.current;
    if (!p) return;
    registerPlaybackPlayer(p);
    return () => {
      registerPlaybackPlayer(null);
    };
  }, [_player, registerPlaybackPlayer]);

  useEffect(() => {
    // TODO: this or similar code exists in many places (here, in AssetViewer.tsx and in ControlBar.tsx)
    // We need to do this once and then just pass "currentSource" and "playableSources" as props
    // TBD: not sure if this need to be in AssetViewer.tsx or here.
    // Probably here, because it is responsibility of video player to decide what and when it can play.
    // Then we can just utilize onReady callback to set "isPreparing" in AssetViewer.tsx.
    // In that case pass "isPreparing" as prop is also redundant.
    const player = playerRef.current;
    if (!player) return;
    player.pause();
    if (isPreparing) return;

    if (asset.metadata.type === 'video') {
      const readyProxies = Object.entries(asset.metadata.videoProxies)
        .map(([key, value]) => ({ key: parseInt(key, 10), value }))
        .filter((el) => el.value.status === 'ready')
        .sort((a, b) => b.key - a.key);
      const defaultProxy =
        readyProxies.find((el) => el.key <= 720) ??
        readyProxies.find((el) => el.key <= 1080);
      if (defaultProxy) {
        player.src({
          type: 'video/mp4',
          src: defaultProxy.value.url
        });
      } else if (checkCanPlaySource(asset)) {
        player.src({
          type: asset.mimeType,
          src: asset.sourceUrl
        });
      }
    } else if (asset.metadata.type === 'audio') {
      if (asset.metadata.audioProxy.url) {
        player.src({
          type: 'video/mp4',
          src: asset.metadata.audioProxy.url
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, isPreparing]);

  const transcription =
    asset.metadata.type === 'video' || asset.metadata.type === 'audio'
      ? asset.metadata.transcription
      : null;

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (transcription?.status !== 'ready') return;
    if (!transcription.webVttUrl) return;
    const versionId = asset.id;
    const trackId = `transcription-${versionId}`;
    const persistenceKey = `active-cc-${versionId}`;
    const isActive =
      trackId === (player as any)._closedCaptionsId ||
      localStorage.getItem(persistenceKey) === trackId;
    const track = player.addRemoteTextTrack(
      {
        id: trackId,
        kind: 'subtitles',
        src: transcription.webVttUrl,
        srclang:
          transcription.language.length > 0 ? transcription.language[0] : 'und',
        label: 'Transcription',
        mode: isActive ? 'showing' : 'hidden'
      },
      false
    );
    return () => {
      const currentTrack = player.remoteTextTracks().getTrackById(trackId);
      (player as any)._closedCaptionsId =
        currentTrack?.mode === 'showing' ? trackId : null;
      player.removeRemoteTextTrack(track);
    };
  }, [asset.id, transcription?.webVttUrl]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (selectedCommentId === 'new' && !playerTime[1]) {
      setPlayerTime([player.currentTime(), null]);
      player.pause();
    }
  }, [selectedCommentId]);

  const controlBar = useMemo(
    () =>
      _player
        ? createPortal(
            <ControlBar
              player={_player}
              asset={asset}
              setSelectedCollapse={setSelectedCollapse}
            />,
            _player.el()
          )
        : null,
    [_player, asset, setSelectedCollapse]
  );

  return (
    <div className="video_card_container">
      <div
        ref={videoContainerRef}
        style={{ width: '100%', position: 'relative' }}
      >
        {!isMdWidth && controlBar}
        {isPreparing && <PlaybackPreparingLoader isVideo={true} />}
        {isAssetReady && <FabricCanvas />}
      </div>
    </div>
  );
});

export default VideoCard;
